import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, LogOut, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ADMIN } from "@/constants/testIds";
import {
  verifyAdmin,
  setAdminToken,
  getAdminToken,
  clearAdminToken,
  fetchAppointments,
} from "@/lib/api";

export default function Admin() {
  const [token, setToken] = useState(() => getAdminToken());
  const [authed, setAuthed] = useState(() => !!getAdminToken());
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const appts = await fetchAppointments();
      setAppointments(appts || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        clearAdminToken();
        setAuthed(false);
      } else {
        toast.error("Could not load appointments.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const login = async (e) => {
    e?.preventDefault();
    if (!token) return toast.error("Enter the admin token.");
    setLoading(true);
    try {
      await verifyAdmin(token);
      setAdminToken(token);
      setAuthed(true);
    } catch {
      toast.error("Invalid token.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAdminToken();
    setToken("");
    setAuthed(false);
    setAppointments([]);
  };

  if (!authed) {
    return (
      <main data-testid={ADMIN.page} className="max-w-md mx-auto px-6 py-20">
        <Card className="rounded-3xl border-slate-200">
          <CardContent className="p-8">
            <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary))]/20 grid place-items-center text-slate-900 mb-5">
              <Lock size={20} />
            </div>
            <h1 className="font-serif-display text-3xl text-slate-900">Appointments</h1>
            <p className="text-slate-600 text-sm mt-2">
              Enter your admin token to view appointment requests.
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Content (articles, videos, medications, diseases) is managed in the Namya Admin Panel.
            </p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor={ADMIN.tokenInput}>Admin token</Label>
                <Input
                  id={ADMIN.tokenInput}
                  data-testid={ADMIN.tokenInput}
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                data-testid={ADMIN.loginBtn}
                disabled={loading}
                className="w-full rounded-full bg-[hsl(var(--primary-bold))] text-slate-900 font-semibold hover:bg-[hsl(var(--primary-bold))]/90 h-11"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main data-testid={ADMIN.page} className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-slate-500">Admin</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-slate-900">
            Appointment requests
          </h1>
          <p className="text-slate-500 text-sm mt-2">{appointments.length} total</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Refresh
          </Button>
          <Button
            data-testid={ADMIN.logoutBtn}
            variant="outline"
            onClick={logout}
            className="rounded-full"
          >
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <Table data-testid={ADMIN.bookingsTable}>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Concern</TableHead>
              <TableHead>Slot</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Received</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-10">
                  No appointment requests yet.
                </TableCell>
              </TableRow>
            ) : (
              appointments.map((a) => (
                <TableRow key={a.id} data-testid={ADMIN.bookingRow(a.id)}>
                  <TableCell className="font-medium text-slate-900">
                    {a.name}
                    {a.age ? <span className="text-slate-400 text-xs ml-2">{a.age}y</span> : null}
                  </TableCell>
                  <TableCell>{a.phone}</TableCell>
                  <TableCell>
                    {a.condition ? (
                      <Badge variant="secondary" className="bg-emerald-50 text-slate-700 border-emerald-100">
                        {a.condition}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <div>{a.preferred_date}</div>
                    <div className="text-xs text-slate-500">{a.preferred_time || ""}</div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="line-clamp-2 text-sm text-slate-600">
                      {a.message || "—"}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {a.created_at ? format(new Date(a.created_at), "dd MMM, HH:mm") : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </main>
  );
}
